// frontend/admin-portal/src/components/products/ProductModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal, Button, Input, Alert } from "@fiberhome/shared-ui";
import {
  ProductFormRenderer,
  StockFieldsGroup,
  ShipmentContextSection,
  PricingCalculator,
  getSchemaForCategory,
} from "@fiberhome/shared-ui";
import { productService } from "../../services/productService";
import ProductImagesInline from "./ProductImagesInline";
import DefaultAccountBadge from "../treasury/DefaultAccountBadge";

const BASE_EMPTY = {
  name: "",
  sku: "",
  barcode: "",
  categoryId: "",
  brandId: "",
  price: 0,
  costPrice: 0,
  totalCost: 0,
  shipmentRate: 0,
  volumeCbm: 0,
  taxRate: 0,
  slug: "",
  currency: "USD",
  stock: 0,
  minStock: 0,
  unit: "pcs",
  description: "",
  shortDescription: "",
  attributes: {},
  images: [],
  isActive: true,
  isFeatured: false,
  trackStock: true,
};

function normalizeAttributes(attrs) {
  if (!attrs) return {};
  if (typeof attrs === "string") {
    try {
      return JSON.parse(attrs) || {};
    } catch {
      return {};
    }
  }
  return typeof attrs === "object" ? attrs : {};
}

/**
 * Compute cbmPerUnit from form attributes, matching the logic in
 * PricingCalculator. Returns 0 if no valid data.
 */
function deriveCbmPerUnit(form) {
  const attrs = form.attributes || {};
  const pkgL = Number(attrs.packageLength_cm) || 0;
  const pkgW = Number(attrs.packageWidth_cm) || 0;
  const pkgH = Number(attrs.packageHeight_cm) || 0;
  const pkgUnits = Number(attrs.unitsPerPackage) || 0;

  if (pkgL > 0 && pkgW > 0 && pkgH > 0 && pkgUnits > 0) {
    // Package mode: (L × W × H) cm³ → CBM, divided by units
    const pkgCbm = (pkgL * pkgW * pkgH) / 1000000;
    return pkgCbm / pkgUnits;
  }

  // Direct CBM mode
  return Number(form.volumeCbm) || 0;
}

/**
 * Read the current shipment rate from localStorage.
 * This is the source of truth — the PricingCalculator's hook value
 * comes from the same place but might lag behind saves.
 */
function readShipmentRateFromStorage() {
  try {
    const raw = localStorage.getItem("fiberhome.shipmentContext");
    if (!raw) return 0;
    const ctx = JSON.parse(raw) || {};

    const totalCbm = Number(ctx.totalCbm) || 0;
    const totalShipmentCost = Number(ctx.totalShipmentCost) || 0;
    const costPerCbm = Number(ctx.costPerCbm) || 0;

    const freightPerCbm =
      totalCbm > 0 ? totalShipmentCost / totalCbm : costPerCbm;

    const overhead =
      Number(ctx.rent || 0) +
      Number(ctx.customs || 0) +
      Number(ctx.insurance || 0) +
      Number(ctx.misc || 0);
    const overheadPerCbm = totalCbm > 0 ? overhead / totalCbm : 0;

    return Number((freightPerCbm + overheadPerCbm).toFixed(2));
  } catch {
    return 0;
  }
}

export default function ProductModal({
  open,
  product,
  isDuplicate = false,
  categories = [],
  brands = [],
  lowStockThreshold = null,
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(BASE_EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const skuOverriddenRef = useRef(false);
  const nameOverriddenRef = useRef(false);

  const selectedCategory = useMemo(
    () => categories.find((c) => String(c.id) === String(form.categoryId)),
    [categories, form.categoryId],
  );

  const schema = useMemo(
    () => getSchemaForCategory(selectedCategory),
    [selectedCategory],
  );

  // Seed form on open
  useEffect(() => {
    if (!open) return;
    setError("");

    const hasExistingName =
      !!product && !!(product.name && String(product.name).trim());
    const hasExistingSku =
      !!product && !!(product.sku && String(product.sku).trim());

    nameOverriddenRef.current = hasExistingName;
    skuOverriddenRef.current = hasExistingSku;

    if (product) {
      setForm({
        ...BASE_EMPTY,
        ...product,
        ...(isDuplicate ? { sku: "", barcode: "", stock: 0, slug: "" } : {}),
        attributes: normalizeAttributes(product.attributes),
        categoryId: product.categoryId ?? product.category?.id ?? "",
        brandId: product.brandId ?? product.brand?.id ?? "",
        images: Array.isArray(product.images) ? product.images : [],
        volumeCbm: Number(product.volumeCbm) || 0,
        price: Number(product.price) || 0,
        costPrice: Number(product.costPrice) || 0,
        totalCost: Number(product.totalCost) || 0,
        shipmentRate: 0,
        stock: isDuplicate ? 0 : Number(product.stock) || 0,
        minStock: Number(product.minStock) || 0,
      });
    } else {
      setForm(BASE_EMPTY);
    }
  }, [open, product, isDuplicate]);

  // Apply schema default unit
  useEffect(() => {
    if (!schema?.defaultUnit) return;
    setForm((f) =>
      f.unit === "pcs" && schema.defaultUnit !== "pcs"
        ? { ...f, unit: schema.defaultUnit }
        : f,
    );
  }, [schema]);

  // Setter — handles dotted keys
  const set = (key, value) => {
    if (key.includes(".")) {
      const [parent, ...rest] = key.split(".");
      const child = rest.join(".");
      setForm((f) => ({
        ...f,
        [parent]: { ...(f[parent] || {}), [child]: value },
      }));
    } else {
      setForm((f) => ({ ...f, [key]: value }));
    }
  };

  const setAttribute = (key, value) =>
    setForm((f) => ({
      ...f,
      attributes: { ...f.attributes, [key]: value },
    }));

  // Auto-generate SKU + name
  useEffect(() => {
    if (!schema || !open) return;
    setForm((f) => {
      const attrs = f.attributes || {};
      const patch = {};
      if (schema.buildSku && !skuOverriddenRef.current) {
        const next = schema.buildSku(attrs);
        if (next && next !== f.sku) patch.sku = next;
      }
      if (schema.buildName && !nameOverriddenRef.current) {
        const next = schema.buildName(attrs);
        if (next && next !== f.name) patch.name = next;
      }
      return Object.keys(patch).length ? { ...f, ...patch } : f;
    });
  }, [form.attributes, schema, open]);

  function payload_sku(sku, suffix) {
    if (!sku) return sku;
    if (!suffix) return sku;
    return `${sku}${suffix}`;
  }

  function payload_slug(slug, suffix) {
    if (!slug) return slug;
    if (!suffix) return slug;
    return `${slug}${suffix}`;
  }

  // ─────────────────────────────────────────────────────────────
  // Submit
  // ─────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    setSaving(true);
    setError("");

    const isCreate = !product?.id || isDuplicate;

    const baseSlug =
      form.slug?.trim() ||
      (form.name
        ? form.name
            .toLowerCase()
            .trim()
            .replace(/[^\w\s-]/g, "")
            .replace(/[\s_-]+/g, "-")
            .replace(/^-+|-+$/g, "")
        : "");

    // ── Compute all derived cost fields from RAW form data ─────
    // Do NOT trust form.volumeCbm or form.shipmentRate — they might
    // be stale if the calculator's effects haven't fired yet.
    const cbmPerUnit = deriveCbmPerUnit(form);
    const rate = readShipmentRateFromStorage();
    const fob = Number(form.costPrice) || 0;
    const computedTotalCost =
      cbmPerUnit > 0 && rate > 0
        ? Number((fob + cbmPerUnit * rate).toFixed(2))
        : fob;

    // 🐛 Temporary debug — remove after confirming fix
    // eslint-disable-next-line no-console
    console.log("[ProductModal] submit cost derivation:", {
      fob,
      cbmPerUnit,
      rate,
      computedTotalCost,
      rawAttributes: form.attributes,
    });

    const basePayload = {
      ...form,
      name: form.name?.trim(),
      sku: form.sku?.trim(),
      categoryId: Number(form.categoryId),
      brandId: form.brandId ? Number(form.brandId) : null,
      barcode: form.barcode?.trim() ? form.barcode.trim() : null,
      slug: baseSlug || null,
      shortDescription: form.shortDescription?.trim() || null,
      description: form.description?.trim() || null,
      price: Number(form.price) || 0,
      costPrice: fob,
      // Send the derived values, not the possibly-stale form values
      volumeCbm: cbmPerUnit,
      shipmentRate: rate,
      totalCost: computedTotalCost,
      taxRate: Number(form.taxRate) || 0,
      stock: Number(form.stock) || 0,
      minStock: Number(form.minStock) || 0,
    };

    let attempt = 0;
    let lastErr = null;

    while (attempt < 5) {
      attempt += 1;
      const suffix =
        attempt === 1 ? "" : `-${Date.now().toString(36).slice(-4)}-${attempt}`;

      const payload = {
        ...basePayload,
        sku: payload_sku(basePayload.sku, suffix),
        slug: payload_slug(basePayload.slug, suffix),
      };

      try {
        if (!isCreate && product?.id) {
          await productService.update(product.id, payload);
        } else {
          await productService.create(payload);
        }

        onSaved?.();
        onClose?.();
        setSaving(false);
        return;
      } catch (err) {
        lastErr = err;
        const status = err.response?.status;
        if (status !== 409) break;
        const field = err.response?.data?.field;
        if (field && !["sku", "slug", "barcode"].includes(field)) break;
      }
    }

    setError(
      lastErr?.response?.data?.message ||
        lastErr?.message ||
        "Save failed — check console for details",
    );
    setSaving(false);
  };

  // Render — unchanged
  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        isDuplicate
          ? `Duplicate of: ${product?.name || product?.sku || "Product"}`
          : product?.id
            ? `Edit: ${product?.name || product?.sku || "Product"}`
            : "New Product"
      }
      size="xl"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} loading={saving}>
            {isDuplicate
              ? "Create Copy"
              : product?.id
                ? "Save Changes"
                : "Create Product"}
          </Button>
        </>
      }
    >
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}
      {isDuplicate && (
        <Alert variant="info" className="mb-3">
          <strong>Duplicating:</strong> {product?.name || "Product"}. SKU and
          barcode are cleared — you'll need to enter new values (or let the
          system auto-generate the SKU). Stock starts at 0.
        </Alert>
      )}

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <label className="fh-input-label">Category *</label>
          <select
            className="fh-input"
            value={form.categoryId}
            onChange={(e) => set("categoryId", e.target.value)}
          >
            <option value="">Select category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-6">
          <label className="fh-input-label">Brand</label>
          <select
            className="fh-input"
            value={form.brandId || ""}
            onChange={(e) => set("brandId", e.target.value)}
          >
            <option value="">No brand</option>
            {brands.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="row g-3 mb-3">
        <div className="col-12">
          <ProductImagesInline
            value={form.images}
            onChange={(imgs) => set("images", imgs)}
            entityType="products"
            entityId={product?.id || null}
          />
        </div>
      </div>

      {schema ? (
        <div className="mb-3">
          <div className="fh-modal-section-title">
            {schema.label} specifications
          </div>
          <ProductFormRenderer
            schema={schema}
            values={form.attributes}
            onChange={setAttribute}
          />
        </div>
      ) : (
        selectedCategory && (
          <Alert variant="info" className="mb-3">
            No custom form is available for this category — you'll only be able
            to enter the core product fields below.
          </Alert>
        )
      )}

      <div className="mb-3">
        <ShipmentContextSection expanded={false} />
      </div>

      <div className="fh-modal-section-title">Pricing</div>
      <div className="mb-3">
        <PricingCalculator
          form={form}
          onChange={(key, value) => set(key, value)}
          attributes={form.attributes}
          costDefaults={schema?.costDefaults || null}
        />
        <div className="mt-2">
          <DefaultAccountBadge />
        </div>
      </div>

      <div className="fh-modal-section-title mt-3">Identity</div>
      <div className="row g-3 mb-3">
        <div className="col-md-8">
          <Input
            label="Product Name *"
            value={form.name}
            onChange={(e) => {
              nameOverriddenRef.current = true;
              set("name", e.target.value);
            }}
            hint="Auto-generated from specs; you can override"
          />
        </div>
        <div className="col-md-4">
          <Input
            label="SKU"
            value={form.sku}
            onChange={(e) => {
              skuOverriddenRef.current = true;
              set("sku", e.target.value);
            }}
            hint="Auto-generated; leave to accept preview"
          />
        </div>
      </div>

      <div className="fh-modal-section-title">Stock</div>
      <StockFieldsGroup
        stock={form.stock}
        minStock={form.minStock}
        unit={form.unit}
        onStockChange={(v) => set("stock", v)}
        onMinStockChange={(v) => set("minStock", v)}
        onUnitChange={(v) => set("unit", v)}
        lowStockThreshold={lowStockThreshold}
      />

      <div className="row g-3 mt-1">
        <div className="col-md-6">
          <Input
            label="Barcode"
            value={form.barcode}
            onChange={(e) => set("barcode", e.target.value)}
          />
        </div>
        <div className="col-md-6">
          <Input
            label="Short Description"
            value={form.shortDescription}
            onChange={(e) => set("shortDescription", e.target.value)}
          />
        </div>
        <div className="col-12">
          <Input
            label="Description"
            value={form.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
        <div className="col-12 d-flex gap-4 flex-wrap">
          <label className="fh-checkbox">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) => set("isActive", e.target.checked)}
            />
            <span>Active</span>
          </label>
          <label className="fh-checkbox">
            <input
              type="checkbox"
              checked={form.isFeatured}
              onChange={(e) => set("isFeatured", e.target.checked)}
            />
            <span>Featured</span>
          </label>
          <label className="fh-checkbox">
            <input
              type="checkbox"
              checked={form.trackStock}
              onChange={(e) => set("trackStock", e.target.checked)}
            />
            <span>Track stock</span>
          </label>
        </div>
      </div>
    </Modal>
  );
}
